import React, { useState } from 'react';
import { Alert, Badge, Button, Form, Modal } from 'react-bootstrap';
import { Formik } from 'formik';
import * as Yup from 'yup';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { setRompslompCreditVerified, setRompslompPause, setRompslompSending } from '../../../services/RompslompService';
import { apiMessage } from './rompslompUtils';
import RompslompSentAs, { useRompslompLookup } from './RompslompSentAs';

// Asks for the user's CURRENT password (same pattern as Test mode) before a risky switch.
export function PasswordModal({ show, title, text, confirmLabel, onConfirm, onHide }) {
    const { t } = useTranslation();
    const schema = Yup.object({ password: Yup.string().required(t('rompslomp.sending.passwordRequired')) });
    return (
        <Modal show={show} onHide={onHide} centered>
            <Formik initialValues={{ password: '' }} validationSchema={schema}
                onSubmit={async (values, { setSubmitting, resetForm }) => { try { await onConfirm(values.password); resetForm(); } finally { setSubmitting(false); } }}>
                {({ handleSubmit, handleChange, handleBlur, values, errors, touched, isSubmitting }) => (
                    <Form noValidate onSubmit={handleSubmit}>
                        <Modal.Header closeButton><Modal.Title>{title}</Modal.Title></Modal.Header>
                        <Modal.Body>
                            <p>{text}</p>
                            <Form.Group controlId="rompslomp-sending-password">
                                <Form.Label>{t('rompslomp.sending.password')}</Form.Label>
                                <Form.Control type="password" name="password" autoComplete="current-password" value={values.password}
                                    onChange={handleChange} onBlur={handleBlur} isInvalid={touched.password && !!errors.password} />
                                <Form.Control.Feedback type="invalid">{errors.password}</Form.Control.Feedback>
                            </Form.Group>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="light" onClick={onHide}>{t('common.cancel', { defaultValue: 'Cancel' })}</Button>
                            <Button type="submit" variant="danger" disabled={isSubmitting}>{confirmLabel}</Button>
                        </Modal.Footer>
                    </Form>
                )}
            </Formik>
        </Modal>
    );
}

// Shows the EXACT payload before it is sent. The hash makes the server refuse it if the payload changed meanwhile.
export function SendConfirmModal({ show, title, payload, hash, busy, onConfirm, onHide }) {
    const { t } = useTranslation();
    const lookup = useRompslompLookup(show);
    return (
        <Modal show={show} onHide={onHide} size="lg" centered>
            <Modal.Header closeButton><Modal.Title>{title}</Modal.Title></Modal.Header>
            <Modal.Body>
                <Alert variant="warning">{t('rompslomp.sending.confirmText')}</Alert>
                <pre className="rompslomp-payload" data-testid="confirm-payload">{JSON.stringify(payload, null, 2)}</pre>
                <RompslompSentAs payload={payload} lookup={lookup} />
                <small className="text-muted">{t('rompslomp.sending.hash')}: {hash}</small>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="light" onClick={onHide}>{t('common.cancel', { defaultValue: 'Cancel' })}</Button>
                <Button variant="danger" onClick={onConfirm} disabled={busy} data-testid="confirm-send">{t('rompslomp.sending.sendNow')}</Button>
            </Modal.Footer>
        </Modal>
    );
}

// Banner shown on every Rompslomp screen: SENDING OFF / ON (+ paused) and the administration.
export function SendingBanner({ sending, company }) {
    const { t } = useTranslation();
    const state = sending.paused ? 'paused' : (sending.enabled ? 'on' : 'off');
    return (
        <Alert variant={state === 'on' ? 'danger' : (state === 'paused' ? 'warning' : 'secondary')} data-testid="sending-banner">
            <strong>{t(`rompslomp.sending.banner.${state}`)}</strong>
            {company ? <> · {t('rompslomp.banner.company')}: <strong>{company.name}</strong></> : null}
            {sending.paused && sending.pause_reason ? <div className="small">{sending.pause_reason}</div> : null}
            {sending.auto?.enabled ? <div className="small" data-testid="auto-banner-on">{t('rompslomp.auto.bannerOn')}</div> : null}
            {sending.auto?.stopped_reason ? <div className="fw-bold mt-1" data-testid="auto-banner-stopped">{t('rompslomp.auto.bannerStopped', { reason: sending.auto.stopped_reason })}</div> : null}
        </Alert>
    );
}

export function SendingControls({ sending, reload }) {
    const { t } = useTranslation();
    const [ask, setAsk] = useState(null);     // 'sending' | 'resume' | 'credit'

    const run = async (call, okKey) => {
        try { await call(); toast.success(t(okKey)); setAsk(null); reload(); } catch (err) { toast.error(apiMessage(err, t, 'rompslomp.errors.generic')); }
    };
    const pauseNow = () => run(() => setRompslompPause(true), 'rompslomp.sending.pausedDone');

    return (
        <div className="rompslomp-sending-controls" data-testid="sending-controls">
            <p className="text-muted">{t('rompslomp.sending.hint')}</p>
            <div className="d-flex flex-wrap gap-2 align-items-center mb-3">
                <Button variant={sending.enabled ? 'outline-secondary' : 'danger'} onClick={() => setAsk('sending')} data-testid="toggle-sending">
                    {sending.enabled ? t('rompslomp.sending.switchOff') : t('rompslomp.sending.switchOn')}
                </Button>
                {sending.paused
                    ? <Button variant="outline-warning" onClick={() => setAsk('resume')}>{t('rompslomp.sending.resume')}</Button>
                    : <Button variant="warning" onClick={pauseNow} disabled={!sending.enabled} data-testid="pause-now">{t('rompslomp.sending.pause')}</Button>}
                <Badge bg="secondary">{t('rompslomp.sending.batchState', { state: sending.batch_unlocked ? t('rompslomp.sending.unlocked') : t('rompslomp.sending.locked') })}</Badge>
            </div>
            <Form.Check type="switch" id="credit-verified" checked={sending.credit_verified} onChange={() => setAsk('credit')}
                label={t('rompslomp.sending.creditVerified')} />
            <Form.Text className="text-muted d-block">{t('rompslomp.sending.creditHint')}</Form.Text>

            <PasswordModal show={ask === 'sending'} onHide={() => setAsk(null)}
                title={sending.enabled ? t('rompslomp.sending.switchOff') : t('rompslomp.sending.switchOn')}
                text={sending.enabled ? t('rompslomp.sending.offText') : t('rompslomp.sending.onText')}
                confirmLabel={sending.enabled ? t('rompslomp.sending.switchOff') : t('rompslomp.sending.switchOn')}
                onConfirm={(pw) => run(() => setRompslompSending(!sending.enabled, pw), 'rompslomp.sending.switched')} />
            <PasswordModal show={ask === 'resume'} onHide={() => setAsk(null)} title={t('rompslomp.sending.resume')} text={t('rompslomp.sending.resumeText')}
                confirmLabel={t('rompslomp.sending.resume')} onConfirm={(pw) => run(() => setRompslompPause(false, pw), 'rompslomp.sending.switched')} />
            <PasswordModal show={ask === 'credit'} onHide={() => setAsk(null)} title={t('rompslomp.sending.creditVerified')} text={t('rompslomp.sending.creditText')}
                confirmLabel={t('rompslomp.sending.confirm')} onConfirm={(pw) => run(() => setRompslompCreditVerified(!sending.credit_verified, pw), 'rompslomp.sending.switched')} />
        </div>
    );
}
